import React, { useMemo, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Tooltip, Button } from '@mui/material';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import '../styles/PricingComparisonChart.css';
import { getAssessmentKeywords } from '../utils/assessmentKeywords';

const colors = {
  primary: '#2ecc71',    // Green for receptive
  neutral: '#d5d8dc',    // Light gray for neutral
  negative: '#e74c3c',   // Red for resistant
};

const PricingBarChart = ({ label, receptive, neutral, resistant, onClick }) => {
  // Round percentages to ensure they sum to 100
  const roundedReceptive = Math.round(receptive);
  const roundedNeutral = Math.round(neutral);
  const roundedResistant = Math.round(resistant);
  
  const handleClick = (type) => {
    onClick({
      label,
      type,
      receptive: roundedReceptive,
      neutral: roundedNeutral,
      resistant: roundedResistant
    });
  };
  
  return (
    <div className="pricing-comparison-item">
      <div className="pricing-company-name">{label}</div>
      <Tooltip
        title={
          <div className="percentage-tooltip">
            <div>{roundedReceptive}% Open to Price Increase</div>
            <div>{roundedNeutral}% Indifferent</div>
            <div>{roundedResistant}% Price Sensitive</div>
          </div>
        }
        placement="right"
        arrow
      >
        <div className="bar-chart-container">
          <div className="bar-container">
            {receptive > 0 && (
              <div
                className="bar-segment positive"
                style={{ width: `${receptive}%` }}
                title={`${roundedReceptive}% Open to Price Increase`}
                onClick={() => handleClick('receptive')}
              />
            )}
            {neutral > 0 && (
              <div
                className="bar-segment neutral"
                style={{ width: `${neutral}%` }}
                title={`${roundedNeutral}% Indifferent`}
                onClick={() => handleClick('neutral')}
              />
            )}
            {resistant > 0 && (
              <div
                className="bar-segment negative"
                style={{ width: `${resistant}%` }}
                title={`${roundedResistant}% Price Sensitive`}
                onClick={() => handleClick('resistant')}
              />
            )}
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

const PricingComparisonChart = ({ topicMap, selectedProductId }) => {
  console.log("PricingComparisonChart rendered with:", {
    topicMapSize: topicMap ? Object.keys(topicMap).length : 0,
    selectedProductId,
    hasProducts: topicMap ? Object.values(topicMap).filter(t => t.relationship === 'product').length : 0
  });
  
  const [postsReferenced, setPostsReferenced] = useState([]);
  const [showPostDisplay, setShowPostDisplay] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if we have competitor data
  const hasCompetitorData = useMemo(() => {
    if (!topicMap) return false;
    
    // Check if any product has competitors with pricing data
    return Object.values(topicMap).some(topic => 
      topic.relationship === 'product' && 
      topic.competitors?.some(comp => comp.assessments?.pricing && comp.assessments.pricing.length > 0)
    );
  }, [topicMap]);

  // Filter function for posts
  const filterPosts = (topic, segmentType) => {
    if (!topic?.assessments?.pricing) return [];
    
    const pricingData = topic.assessments.pricing;
    const thresholds = {
      veryReceptive: 7,
      moderatelyReceptive: 3,
      notReceptive: -3,
      extremelyNotReceptive: -7
    };
    
    return pricingData.filter(item => {
      if (item.rating === null) return false;
      
      switch(segmentType) {
        case 'receptive':
          return item.rating > thresholds.moderatelyReceptive;
        case 'neutral':
          return item.rating <= thresholds.moderatelyReceptive && item.rating > thresholds.notReceptive;
        case 'resistant':
          return item.rating <= thresholds.notReceptive;
        default:
          return false;
      }
    }).map(post => ({
      ...post,
      ratingName: 'pricing',
      ratingType: 'pricing'
    }));
  };
  
  // Process data from topicMap to get pricing data for main products and competitors
  const chartData = useMemo(() => {
    if (!topicMap || Object.keys(topicMap).length === 0) {
      console.log("chartData: No topicMap data");
      return null;
    }

    // Get product topics filtered by the selected product (if any)
    let productTopics = Object.values(topicMap).filter(
      topic => topic.relationship === 'product' && topic.assessments?.pricing
    );
    
    console.log("chartData: Initial product topics:", {
      count: productTopics.length,
      ids: productTopics.map(t => t.id),
      selectedProductId
    });

    // If a product is selected, only show that product and its competitors
    if (selectedProductId) {
      productTopics = productTopics.filter(topic => topic.id === selectedProductId);
      console.log("chartData: Filtered to selected product:", {
        count: productTopics.length,
        ids: productTopics.map(t => t.id)
      });
    }

    if (productTopics.length === 0) {
      console.log("chartData: No product topics after filtering");
      return null;
    }

    const items = [];

    // Thresholds for categorizing ratings (same as in PricingPlot.js)
    const thresholds = {
      veryReceptive: 7,
      moderatelyReceptive: 3,
      notReceptive: -3,
      extremelyNotReceptive: -7
    };

    // Process each product topic and its competitors
    productTopics.forEach(topic => {
      // Add main product
      if (topic.assessments?.pricing) {
        const pricingData = topic.assessments.pricing;
        
        // Calculate sentiment distribution
        let receptiveCount = 0;
        let neutralCount = 0;
        let resistantCount = 0;
        let totalCount = 0;
        
        pricingData.forEach(item => {
          if (item.rating !== null) {
            totalCount++;
            
            if (item.rating > thresholds.moderatelyReceptive) {
              receptiveCount++; // Very receptive + moderately receptive
            } else if (item.rating > thresholds.notReceptive) {
              neutralCount++; // Neutral
            } else {
              resistantCount++; // Not receptive + extremely not receptive
            }
          }
        });
        
        // Only add if we have data
        if (totalCount > 0) {
          items.push({
            id: topic.id,
            label: topic.name,
            receptive: (receptiveCount / totalCount) * 100,
            neutral: (neutralCount / totalCount) * 100,
            resistant: (resistantCount / totalCount) * 100,
            topic: topic,
            isMainProduct: true
          });
        }
      }
      
      // Add competitors for this product (only if expanded)
      if (isExpanded) {
        topic.competitors.forEach(competitor => {
          if (competitor.assessments?.pricing) {
            const pricingData = competitor.assessments.pricing;
            
            // Calculate sentiment distribution
            let receptiveCount = 0;
            let neutralCount = 0;
            let resistantCount = 0;
            let totalCount = 0;
            
            pricingData.forEach(item => {
              if (item.rating !== null) {
                totalCount++;
                
                if (item.rating > thresholds.moderatelyReceptive) {
                  receptiveCount++; // Very receptive + moderately receptive
                } else if (item.rating > thresholds.notReceptive) {
                  neutralCount++; // Neutral
                } else {
                  resistantCount++; // Not receptive + extremely not receptive
                }
              }
            });
            
            // Only add if we have data
            if (totalCount > 0) {
              // Use competitor alt_name if available, otherwise fallback to topic
              const displayName = competitor.alt_name || competitor.topic;
              items.push({
                id: competitor.id,
                label: displayName,
                receptive: (receptiveCount / totalCount) * 100,
                neutral: (neutralCount / totalCount) * 100,
                resistant: (resistantCount / totalCount) * 100,
                topic: competitor,
                isMainProduct: false
              });
            }
          }
        });
      }
    });

    // Only return data if we have products to compare
    if (items.length === 0) {
      return null;
    }

    return items;
  }, [topicMap, isExpanded, selectedProductId]);

  const handleBarClick = (barData) => {
    // Find the topic or competitor
    const item = chartData.find(item => item.label === barData.label);
    
    if (item && item.topic) {
      const filteredPosts = filterPosts(item.topic, barData.type);
      
      if (filteredPosts.length > 0) {
        setPostsReferenced(filteredPosts);
        setSelectedSegment({
          label: `${item.label} - ${barData.type === 'receptive' ? 'Open to Price Increase' : 
                                 barData.type === 'neutral' ? 'Indifferent' : 'Price Sensitive'}`,
          percentage: barData.type === 'receptive' ? barData.receptive : 
                      barData.type === 'neutral' ? barData.neutral : barData.resistant
        });
        setShowPostDisplay(true);
      }
    }
  };

  const handleClosePostsReferenced = () => {
    setShowPostDisplay(false);
    setPostsReferenced([]);
    setSelectedSegment(null);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // If no chart data, return a message
  if (!chartData) {
    return null;
  }

  const renderLegend = () => (
    <div className="pricing-chart-legend">
      <div className="pricing-legend-container">
        <div className="pricing-legend-item">
          <span className="pricing-legend-dash pricing-positive"></span>
          <span className="pricing-legend-text">Open to Price Increase</span>
        </div>
        <div className="pricing-legend-item">
          <span className="pricing-legend-dash pricing-neutral"></span>
          <span className="pricing-legend-text">Indifferent</span>
        </div>
        <div className="pricing-legend-item">
          <span className="pricing-legend-dash pricing-negative"></span>
          <span className="pricing-legend-text">Price Sensitive</span>
        </div>
      </div>
    </div>
  );

  if (!isExpanded && hasCompetitorData) {
    return (
      <div className="pricing-comparison-toggle">
        <Button 
          variant="outlined" 
          startIcon={<KeyboardArrowDownIcon />} 
          onClick={toggleExpanded}
          className="toggle-button"
        >
          Show Competitor Pricing Comparison
        </Button>
      </div>
    );
  }

  return (
    <div className="pricing-comparison-chart">
      <div className="chart-header">
        <h3 className="chart-title">Competitor Pricing Comparison</h3>
        <div className="spacer"></div>
        
        {isExpanded && hasCompetitorData && (
          <Button 
            variant="text" 
            endIcon={<KeyboardArrowUpIcon />} 
            onClick={toggleExpanded}
            className="collapse-button"
          >
            HIDE
          </Button>
        )}
        <Tooltip 
          title="This chart compares customer receptiveness to price increases across products. Higher percentages in green indicate more flexibility for pricing adjustments. Click on any bar segment to see relevant posts."
          placement="top"
        >
          <InfoIcon className="info-icon" fontSize="small" />
        </Tooltip>
      </div>
      
      {renderLegend()}
      
      <div className="comparison-bars">
        {chartData.map((item, index) => (
          <PricingBarChart 
            key={index}
            label={item.label}
            receptive={item.receptive}
            neutral={item.neutral}
            resistant={item.resistant}
            onClick={handleBarClick}
          />
        ))}
      </div>
      
      {showPostDisplay && postsReferenced.length > 0 && (
        <FilteredPostsDisplay 
          posts={postsReferenced}
          onClose={handleClosePostsReferenced}
          id="pricing-posts"
          title={`${selectedSegment?.label} (${Math.round(selectedSegment?.percentage)}%)`}
          ratingDisplayName="Pricing Receptiveness"
          autoScroll={true}
          ratingColorScheme={{
            low: 0,  // ratings <= 0 will be red
            high: 4  // ratings > 4 will be green, ratings between 0-4 will be yellow
          }}
          query={getAssessmentKeywords('pricing')}
        />
      )}
    </div>
  );
};

export default PricingComparisonChart; 